<template>
  <template v-if="companyHasAiEnabled">
    <ModalAiConsent v-model:show="showConsentModal" @ai-terms-agreement-agreed="openChat" />
    <WdsButton id="ai-chat-trigger" secondary :disabled="loading" @click="handleButtonClick">
      <WdsStack align-center gap-sm>
        <WdsIcon :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" :size="18" />
        <slot />
      </WdsStack>
    </WdsButton>
  </template>
  <WdsModal :show="show" md class="ai-chat-modal !tw-overflow-y-hidden" @cancel="show = false">
    <template #header>
      <WdsStack gap-sm align-center>
        <WdsIcon :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" />
        <WdsHeading class="tw-my-0" type="title2">
          {{ $t('Worksome Assistant') }}
        </WdsHeading>
      </WdsStack>
    </template>
    <div v-if="show" class="tw-h-full tw-w-full">
      <WdsDivider class="tw-flex tw-pb-4 tw-text-gray-700">{{ $t('Chat started') }}</WdsDivider>
      <div
        class="tw-flex tw-flex-col tw-gap-2 tw-mb-4 tw-border tw-border-gray-300 tw-rounded tw-max-h-[calc(75vh-120px)] tw-overflow-y-auto tw-w-full"
      >
        <MessageList
          :messages="messages"
          :user-avatar-path="userAvatar"
          class="tw-w-full"
          @update:rating="handleRatingUpdate"
        />
      </div>
      <div class="tw-flex tw-justify-start tw-mb-4">
        <WdsButtonGhost xs :disabled="working || isNewChat" @click="handleClearChat">
          <WdsStack align-center gap-sm>
            <WdsIcon name="erase" :size="14" />
            <span class="tw-text-gray-600">{{ $t('Clear chat') }}</span>
          </WdsStack>
        </WdsButtonGhost>
      </div>
      <form class="tw-max-w-full tw-w-full tw-px-1" @submit.prevent="submit">
        <WdsStack gap-sm justify-center align-center>
          <div class="tw-flex-1 ai-chat-text-input">
            <WdsInput v-model="messageText" :disabled="working" required class="!tw-m-0" />
          </div>
          <WdsButton primary class="tw-flex-0 tw-items-center" :disabled="working">
            <WdsIcon v-if="!working" name="send-alt" :size="18" />
            <WdsSpinner v-if="working" indeterminate />
          </WdsButton>
        </WdsStack>
      </form>
    </div>
  </WdsModal>
</template>

<script lang="ts" setup>
import {
  WdsButton,
  WdsButtonGhost,
  WdsDivider,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsInput,
  WdsModal,
  WdsSpinner,
  WdsStack,
} from '@worksome/wds-components'
import { nextTick, onMounted, ref } from 'vue'
import { getRandomAvatarPath } from '@/utils/avatar'
import MessageList from '@/components/LLM/MessageList.vue'
import ModalAiConsent from '@/components/LLM/ModalAiConsent.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import enums from '@/utils/enum'
import { ai } from '@/api/vue-api'
import { NetworkError } from '@/utils/notification'
import { AiChatContext } from '@/gql/graphql'
import { useAiConsent } from '@/composables/useAiConsent'
import { useAiWelcomeMessage } from '@/composables/useAiWelcomeMessage'

interface Props {
  contextType: AiChatContext
  contextId: number
}

interface Message {
  id: string
  uuid?: string
  isAuthored: boolean
  isPending: boolean
  content: string
  rating?: string | null
  timestamp?: string
  isWelcomeMessage?: boolean
}

const show = ref(false)

const { contextType, contextId } = defineProps<Props>()

const formattedContextType = contextType.toLowerCase()

const userAvatar = getRandomAvatarPath()

const working = ref(false)
const loading = ref(false)
const aiThreadUuid = ref<string | undefined>(undefined)
const messageText = ref('')
const isNewChat = ref(true)

const { createWelcomeMessage } = useAiWelcomeMessage()

const messages = ref<Message[]>([createWelcomeMessage()])

const { needsConsent, showConsentModal } = useAiConsent()
const companyHasAiEnabled = ref(false)

const fetchContext = async () => {
  try {
    const response = await ai.getContext(formattedContextType, contextId)
    aiThreadUuid.value = response.data.ai_thread_uuid
    companyHasAiEnabled.value = response.data.has_consent ?? false
  } catch (error) {
    aiThreadUuid.value = undefined
    companyHasAiEnabled.value = false
  }
}

onMounted(async () => {
  await fetchContext()
})

/**
 * Maps backend messages to frontend format.
 * Backward compatibility: prepends welcome message if first message is from user (new flow).
 */
const mapChatResponse = (response): Message[] => {
  aiThreadUuid.value = response.data.ai_thread_uuid

  const backendMessages: Message[] = response.data.messages.map((m) => ({
    id: crypto.randomUUID(),
    uuid: m.uuid,
    isAuthored: m.role !== 'assistant',
    isPending: false,
    content: m.content,
    rating: m.rating,
    timestamp: m.timestamp,
  }))

  if (backendMessages.length === 0) {
    return [createWelcomeMessage()]
  }

  if (backendMessages[0].isAuthored) {
    return [createWelcomeMessage(), ...backendMessages]
  }

  return backendMessages
}

const loadExistingChat = async () => {
  working.value = true
  const previousMessages = [...messages.value]
  try {
    const response = await ai.askAi(
      formattedContextType,
      contextId,
      null,
      aiThreadUuid.value || null,
    )
    await nextTick()
    messages.value = mapChatResponse(response)
    isNewChat.value = false
  } catch (error) {
    messages.value = previousMessages.length > 0 ? previousMessages : [createWelcomeMessage()]
    // Keep aiThreadUuid so the user can retry loading history without refreshing the page.
    isNewChat.value = false
    new NetworkError({
      msg: $t('An error occurred while loading the chat history.'),
      severity: enums.notification.severity.CRITICAL,
    }).dispatch()
  } finally {
    working.value = false
  }
}

const openChat = async () => {
  show.value = true

  if (aiThreadUuid.value) {
    await loadExistingChat()
  } else {
    messages.value = [createWelcomeMessage()]
    isNewChat.value = true
  }
}

const handleRatingUpdate = (messageUuid: string, newRating: string) => {
  messages.value = messages.value.map((message) => {
    if (message.uuid === messageUuid) {
      return { ...message, rating: newRating }
    }
    return message
  })
}

const handleButtonClick = () => {
  if (needsConsent.value) {
    showConsentModal.value = true
  } else {
    openChat()
  }
}

const simulateClearingDelay = async () => {
  working.value = true
  messages.value = [
    {
      id: crypto.randomUUID(),
      isAuthored: false,
      isPending: true,
      content: $t('Clearing chat...'),
    },
  ]
  await new Promise((resolve) => {
    setTimeout(resolve, 400)
  })
}

const handleClearChat = async () => {
  if (!aiThreadUuid.value) {
    await simulateClearingDelay()
    messages.value = [createWelcomeMessage()]
    isNewChat.value = true
    working.value = false
    return
  }

  const previousMessages = [...messages.value]

  try {
    await simulateClearingDelay()
    await ai.archiveThread(aiThreadUuid.value)
    messages.value = [createWelcomeMessage()]
    aiThreadUuid.value = undefined
    isNewChat.value = true
  } catch (error) {
    messages.value = previousMessages
    new NetworkError({
      msg: $t('An error occurred while clearing the chat.'),
      severity: enums.notification.severity.CRITICAL,
    }).dispatch()
  } finally {
    working.value = false
  }
}

const submit = async () => {
  if (!messageText.value.trim()) {
    return
  }

  working.value = true

  messages.value.push({
    id: crypto.randomUUID(),
    isAuthored: true,
    isPending: false,
    content: messageText.value,
  })

  messages.value.push({
    id: crypto.randomUUID(),
    isAuthored: false,
    isPending: true,
    content: $t('Thinking...'),
  })

  const prompt = messageText.value

  messageText.value = ''

  try {
    const response = await ai.askAi(
      formattedContextType,
      contextId,
      prompt,
      aiThreadUuid.value || null,
    )

    await nextTick()

    messages.value = mapChatResponse(response)
    isNewChat.value = false
  } catch (error) {
    messages.value.pop()
    new NetworkError({
      msg: $t('An error occurred while processing the assistants response.'),
      severity: enums.notification.severity.CRITICAL,
    }).dispatch()
  } finally {
    working.value = false
  }
  await nextTick()

  const promptInput = document.querySelector('.wds-input-label input') as HTMLInputElement

  if (promptInput) {
    promptInput.focus()
  }
}
</script>

<style scoped lang="scss">
/* Fix to make input same height as button */
:deep(.wds-input-label) {
  margin: 0;
}

@media (max-height: 850px) {
  .ai-chat-modal {
    padding: 1rem !important;
  }
}

@media (max-height: 700px) {
  .ai-chat-modal {
    padding: 0.5rem !important;
  }
}

@media (max-height: 500px) {
  .ai-chat-modal {
    padding: 0 !important;
  }
}

@media (max-width: 500px) {
  .ai-chat-text-input {
    max-width: 90%;
  }
  :deep(.wds-input-label) {
    width: auto !important;

    .wds-input {
      min-width: 0 !important;
    }
  }
}

:deep(.wds-modal-header) {
  @apply tw-pb-6;
  @apply tw-border-b;
  @apply tw-border-gray-200;
}
</style>
